import { ReactElement, useState } from "react";
import { Dialog, DialogNavigation, DialogContent } from "../modal/Dialog";
import { useNavigate } from "react-router";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { ProfilePicture } from "../profilePicture/ProfilePicture";
import * as ROUTES from "../../constants/routes";
import { useDebounce } from "use-debounce";
import { Icon, InputText } from "@sellar-io/components";

export type AssignedCustomersConnectionType = {
  id: number;
  Retailer: {
    Business: {
      name: string;
      logoUrl: string | null;
    };
  };
};

export function AssignedCustomersDialog({
  active,
  onDismiss,
  connections,
  termName,
  type,
}: {
  active: boolean;
  onDismiss: () => void;
  connections: AssignedCustomersConnectionType[];
  termName: string;
  type?: string;
}): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const [debouncedSearch] = useDebounce(search, 500);

  const showCustomer = (connection: AssignedCustomersConnectionType) => {
    if (debouncedSearch === "") return true;
    const formattedSearch = debouncedSearch.toLowerCase();
    const name = connection.Retailer.Business.name.toLowerCase();

    return name.indexOf(formattedSearch) >= 0;
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title="Assigned customers"
        description={`The following customers have been assigned '${termName}' ${type}`}
      />
      <DialogContent>
        <div className="flex flex-col gap-4 px-4">
          <div className="flex flex-col gap-2">
            <InputLabel>Search customers</InputLabel>
            <InputContainer>
              <Icon icon="MAGNIFY" />
              <InputText
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputContainer>
          </div>
          <div className="flex max-h-[350px] flex-col overflow-auto rounded-lg border border-zinc-300">
            {connections.map(
              (connection, idx) =>
                showCustomer(connection) && (
                  <div
                    key={connection.id}
                    className={twMerge(
                      "flex cursor-pointer items-center gap-4 p-4 hover:bg-zinc-50",
                      idx + 1 < connections.length &&
                        "border-b border-zinc-300",
                    )}
                    onClick={() =>
                      navigate(
                        ROUTES.CUSTOMER.href({ connectionId: connection.id }),
                      )
                    }
                  >
                    <ProfilePicture
                      logoUrl={connection.Retailer.Business.logoUrl}
                      name={connection.Retailer.Business.name}
                    />
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold">
                        {connection.Retailer.Business.name}
                      </span>
                      <span className="text-sm text-zinc-700">
                        {type === "volume discount"
                          ? "Assigned via price lists"
                          : "Assigned directly"}
                      </span>
                    </div>
                  </div>
                ),
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
